// AIBubble — Chat message bubble for AIPage.
// variant: 'user' = right-aligned orange; 'ai' = left-aligned cream with sparkle avatar.

import { Sparkles } from 'lucide-react';

const T = {
  bg: '#FFF5EE', surface: '#FFFFFF', ink: '#1A0F08', inkDim: '#7A6657',
  primary: '#FF5A3C', accent: '#FFB800', line: 'rgba(26,15,8,0.08)',
  sans: '"Inter Variable","Inter",system-ui,sans-serif',
};

interface AIBubbleProps {
  variant: 'user' | 'ai';
  text: string;
  children?: React.ReactNode; // for embedded AIPlanCard
}

export function AIBubble({ variant, text, children }: AIBubbleProps) {
  if (variant === 'user') {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', maxWidth: '100%' }}>
        <div style={{
          background: T.primary, color: '#fff',
          borderRadius: '20px 20px 4px 20px',
          padding: '12px 16px',
          maxWidth: '80%',
        }}>
          <span style={{ fontFamily: T.sans, fontSize: 14, lineHeight: 1.45 }}>{text}</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', maxWidth: '90%' }}>
      {/* AI avatar */}
      <div style={{
        width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
        background: `linear-gradient(135deg, ${T.primary}, ${T.accent})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Sparkles size={14} stroke="#fff" strokeWidth={1.8}/>
      </div>

      <div style={{
        background: T.surface, borderRadius: '20px 20px 20px 4px',
        padding: '12px 16px', border: `1px solid ${T.line}`,
        maxWidth: '100%',
      }}>
        <span style={{ fontFamily: T.sans, fontSize: 14, color: T.ink, lineHeight: 1.45 }}>{text}</span>
        {children && <div style={{ marginTop: 12 }}>{children}</div>}
      </div>
    </div>
  );
}

// Typing indicator — animated dots
export function AITypingBubble() {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
      <div style={{
        width: 30, height: 30, borderRadius: '50%',
        background: `linear-gradient(135deg, ${T.primary}, ${T.accent})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Sparkles size={14} stroke="#fff" strokeWidth={1.8}/>
      </div>
      <div style={{
        background: T.surface, borderRadius: 20,
        padding: '14px 18px', border: `1px solid ${T.line}`,
        display: 'flex', gap: 4, alignItems: 'center',
      }}>
        {[0, 1, 2].map(d => (
          <div
            key={d}
            className="animate-float"
            style={{
              width: 6, height: 6, borderRadius: '50%', background: T.inkDim,
              animationDelay: `${d * 0.15}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
}
